import { createAsyncThunk } from '@reduxjs/toolkit';

import axios from '../../../foundation/config/axios';

export const getAuthSession = createAsyncThunk('auth/session', async () => {
  const response = await axios.get('/_allauth/browser/v1/auth/session');
  return response.data;
});

export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password }: { username: string; password: string }) => {
    const response = await axios.post(
      '/_allauth/browser/v1/auth/login', // <-- updated endpoint
      { username, password },
    );

    return response.data;
  },
);

// getUserDetails has been migrated to an RTK Query endpoint.
// Use globalDataApi.endpoints.getUserDetails.initiate({ username }) to prefetch,
// or useGetUserDetailsQuery({ username }) in React components.

export const logout = createAsyncThunk('auth/logout', async () => {
  await axios.delete(
    '/_allauth/browser/v1/auth/session', // allauth headless logout has no POST /auth/logout; see underLougout in https://docs.allauth.org/en/latest/headless/openapi-specification/
  );
});

export const getPasswordRules = createAsyncThunk(
  'auth/password-rules',
  async () => {
    const response = await axios.get<string[]>('/users/password-rules');
    return response.data;
  },
);

export const changePassword = createAsyncThunk(
  'auth/change-password',
  async (
    {
      current_password,
      new_password,
    }: {
      current_password: string;
      new_password: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post(
        '/_allauth/browser/v1/account/password/change',
        { current_password, new_password },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  },
);

// PASSWORD RESET DNAWiz
// https://docs.allauth.org/en/dev/headless/openapi-specification/#tag/Authentication:-Password-Reset

// 1. REQUEST PASSWORD RESET: initiates the password reset procedure
// We use a reset link instead of a code (ACCOUNT_PASSWORD_RESET_BY_CODE_ENABLED=False)
// _____________________________________________________________________________________
// Request Body: requires { email: string }
// Response 200: { status: 200 } — success
// Response 400: invalid input { status: 400, errors: ["message", "code"="invalid","param"="email"..] }
export const requestPasswordReset = createAsyncThunk(
  'auth/request-password-reset',
  async ({ email }: { email: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        '/_allauth/browser/v1/auth/password/request',
        { email },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  },
);

// 2. GET PASSWORD RESET INFORMATION — validates the password reset key and returns user info
// The key is the one received from the 1. request-password-reset endpoint (encoded in the password reset URL)
// _____________________________________________________________________________________
// Header parameters- required: X-Password-Reset-Key: <password reset key as string>
// Response 200: Info about the password reset key { status: 200, data: { user: {"id", "display","has_usable_password", "email", "username"} } }
// Response 400: invalid input { status: 400, errors: ["message" ="The password reset token was invalid.", "code"= "token_invalid", "param":"key"..] }
// Response 409: conflict - no password reset flow pending { status: 409, ... }
export const getPasswordResetInfo = createAsyncThunk(
  'auth/password-reset-info',
  async ({ key }: { key: string }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        '/_allauth/browser/v1/auth/password/reset',
        { headers: { 'X-Password-Reset-Key': key } },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        ...error.response?.data,
        httpStatus: error.response?.status,
      });
    }
  },
);

// 3. RESET PASSWORD - performs the password reset with key and new password
// No Auto-login: medical/healthcare apps: re-authentication after password change (HIPAA, BSI TR-03107, ISO 27001)
// _____________________________________________________________________________________
// Request Body -required: { key: string, password: string }
// SUCCESS for FE: Response 401 - { status: 401, data: {...}, meta: { is_authenticated: false } }
//   —  password was successfully reset, but the user is NOT automatically logged in
//   —  The user must proceed to the login page and authenticate with the new password manually
// Response 400 invalid input: { status: 400, errors: [{ "message", "code", "param" }] }
// Response 409: conflict - no password reset flow pending { status: 409, ... }
export const resetPassword = createAsyncThunk(
  'auth/reset-password',
  async (
    {
      key,
      password,
    }: {
      key: string;
      password: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post(
        '/_allauth/browser/v1/auth/password/reset',
        { key, password },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        ...error.response?.data,
        httpStatus: error.response?.status,
      });
    }
  },
);
